import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faCrown, faEye, faEyeSlash, faTrophy } from '@fortawesome/free-solid-svg-icons'
import useReveal from './useReveal'
import { tierOf, formatPct } from './territoryUtils'
import { ANNUAL_AVG_AS_OF } from './territories'

const fmt = n => Number(n || 0).toLocaleString('es-AR')

const Bar = ({ value, color }) => (
  <div className='h-1.5 rounded-full overflow-hidden' style={{ background: 'var(--track)' }}>
    <div className='h-full rounded-full' style={{ width: `${Math.min(1, value) * 100}%`, background: color }} />
  </div>
)

const ConquestPanel = ({ data, liveByKey = {}, visible, setVisible, selectedKey, onSelect, defaultOpen = true }) => {
  // Sigue a `defaultOpen` (si hay lugar al costado del mapa) hasta que el usuario lo abre/cierra a mano.
  const [manualOpen, setManualOpen] = useState(null)
  const open = manualOpen ?? defaultOpen
  const reveal = useReveal(true)

  const list = [...data.territories].sort((a, b) => b.domination - a.domination || b.pace - a.pace)
  const dominatedCount = list.filter(t => t.domination >= 1).length
  const overall = list.length ? list.reduce((acc, t) => acc + Math.min(1, t.domination), 0) / list.length : 0

  return (
    <div
      className='absolute top-3 left-3 z-10 w-[296px] max-w-[calc(100%-24px)] rounded-2xl border overflow-hidden'
      style={{ background: 'var(--panel)', borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)' }}
    >
      <div className='flex items-center gap-2 px-3 pt-2.5 pb-2'>
        <button onClick={() => setManualOpen(!open)} className='flex items-center gap-2 flex-1 min-w-0 text-left'>
          <span className='flex items-center justify-center w-7 h-7 rounded-lg shrink-0' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
            <FontAwesomeIcon icon={faTrophy} className='text-[12px]' />
          </span>
          <span className='font-bold text-[13px] truncate' style={{ color: 'var(--text)' }}>Conquista de rutas</span>
          <span className='flex items-center h-5 px-2 text-[11px] font-bold tabular-nums rounded-full shrink-0' style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>
            {dominatedCount}/{list.length}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className='text-[10px] ml-auto transition-transform duration-200'
            style={{ color: 'var(--text-soft)', transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
          />
        </button>
        <button
          onClick={() => setVisible(v => !v)}
          title={visible ? 'Ocultar territorios del mapa' : 'Mostrar territorios en el mapa'}
          className='w-7 h-7 flex items-center justify-center rounded-lg shrink-0 transition-colors'
          style={{ color: visible ? 'var(--text-soft)' : 'var(--text-faint)', background: 'var(--track)' }}
        >
          <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} className='text-[11px]' />
        </button>
      </div>

      <div className='px-3 pb-2.5'>
        <div className='flex items-baseline justify-between mb-1'>
          <span className='text-[10.5px] uppercase tracking-wider font-medium' style={{ color: 'var(--text-soft)' }}>Territorio dominado</span>
          <span className='text-[13px] font-black tabular-nums' style={{ color: 'var(--text)' }}>{formatPct(overall * reveal)}</span>
        </div>
        <Bar value={overall * reveal} color='linear-gradient(90deg, var(--terr-conquest), var(--terr-dominated))' />
      </div>

      {open && (
        <>
          <div className='live-scroll max-h-[46vh] overflow-y-auto px-1.5 pb-1'>
            {list.map(t => {
              const tier = tierOf(t.domination)
              const dominated = tier.key === 'dominated'
              const live = liveByKey[t.key] || 0
              const selected = selectedKey === t.key
              return (
                <button
                  key={t.key}
                  onClick={() => onSelect && onSelect(t.key)}
                  title={`Últimos 14 días: ${fmt(t.last_14_days)} asientos (≈${fmt(t.monthly_pace)}/mes) · Promedio anual (${ANNUAL_AVG_AS_OF}): ${fmt(t.annual_average)}/mes`}
                  className='w-full text-left px-2 py-2 rounded-xl transition-colors'
                  style={{ background: selected ? 'var(--track)' : 'transparent' }}
                >
                  <div className='flex items-center gap-2'>
                    {dominated ? (
                      <FontAwesomeIcon icon={faCrown} className='text-[10px] shrink-0' style={{ color: tier.color }} />
                    ) : (
                      <span className='w-2 h-2 rounded-full shrink-0' style={{ background: tier.color }} />
                    )}
                    <span className='text-[12.5px] font-semibold truncate flex-1' style={{ color: 'var(--text)' }}>{t.label}</span>
                    <span className='text-[12.5px] font-black tabular-nums shrink-0' style={{ color: tier.color }}>{formatPct(t.domination * reveal)}</span>
                  </div>
                  <div className='mt-1.5'>
                    <Bar value={t.domination * reveal} color={tier.color} />
                  </div>
                  <div className='mt-1 flex items-center gap-1.5 text-[10.5px]' style={{ color: 'var(--text-soft)' }}>
                    <span className='font-semibold' style={{ color: dominated ? tier.color : 'var(--text-soft)' }}>{tier.label}</span>
                    <span style={{ color: 'var(--text-faint)' }}>·</span>
                    <span className='tabular-nums truncate'>{fmt(t.pace)} asientos/mes</span>
                    {live > 0 && (
                      <span className='ml-auto flex items-center gap-1 font-semibold shrink-0' style={{ color: 'var(--coral)' }}>
                        <span className='live-blink w-1.5 h-1.5 rounded-full' style={{ background: 'var(--coral)' }} />
                        {live} en viaje
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>
          <div className='px-3 py-2 text-[10px] leading-snug border-t' style={{ color: 'var(--text-faint)', borderColor: 'var(--border)' }}>
            Dominada = {fmt(data.threshold)}+ asientos vendidos por mes (el mejor entre el ritmo de los últimos 14 días y el promedio anual).
          </div>
        </>
      )}
    </div>
  )
}

export default ConquestPanel
